import { Skeleton } from "@mui/material";
import dayjs from "dayjs";
import {
  Calendar,
  Clock,
  Download,
  Expand,
  Info,
  MapPin,
  Tags,
} from "lucide-react";
import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import { getCompanyAccessName } from "../App";
import HtmlRenderer from "../components/HtmlRenderer";
import ImageSlider from "../components/ImageSlider";
import {
  ButtonContainer,
  Category,
  ClearFix,
  ContentContainer,
  FactText,
  FullscreenIconContainer,
  HtmlRendererContainer,
  ImageContainer,
  KeywordContainer,
  OverlayImage,
  Section,
  SectionTitle,
  StyledButton,
  StyledIconWrapper,
} from "../components/StyledComponents";
import useFetchConfig from "../fetchFunction/ConfigFetch";
import useFetchSingleElements from "../fetchFunction/DetailsFetch";
import "../styles/EventsDetails.css";

const EventDetails = ({ styling, companyAccessname, eventId, onClose }) => {
  const resolvedCompanyAccessname = getCompanyAccessName(companyAccessname);
  const {
    element: eventData,
    loading,
    error,
  } = useFetchSingleElements(resolvedCompanyAccessname, "events", eventId);
  const { configSettings } = useFetchConfig();
  const theme = configSettings?.theme || {};
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isOverlayOpen, setIsOverlayOpen] = useState(false);
  const [selectedImageUrl, setSelectedImageUrl] = useState(null);
  const [imageLoaded, setImageLoaded] = useState(false);

  const event =
    Array.isArray(eventData) && eventData.length > 0 ? eventData[0] : eventData;

  const hasImages =
    event?.image_urls &&
    Array.isArray(event.image_urls) &&
    event.image_urls.length > 0;
  const currentImage = hasImages ? event.image_urls[currentImageIndex] : null;

  useEffect(() => {
    const widgetContent = document.querySelector(
      ".unnpcw-container .widget-content"
    );
    const overlay = document.querySelector(".media-overlay");

    if (widgetContent && overlay) {
      overlay.style.height = `${widgetContent.scrollHeight}px`;
      window.scrollTo(0, 0);
    }
  }, [isOverlayOpen]);

  const openOverlay = (imageUrl) => {
    setSelectedImageUrl(imageUrl);
    setImageLoaded(false);
    setIsOverlayOpen(true);
  };

  const closeOverlay = () => {
    setIsOverlayOpen(false);
    setSelectedImageUrl(null);
  };

  const formatEventDate = (date) =>
    date ? dayjs(date).format("DD.MM.YYYY") : null;
  const formatTime = (time) =>
    time ? time.split(":").slice(0, 2).join(":") : null;

  if (loading) {
    return (
      <div className="chapter-entry">
        <Skeleton variant="text" width="60%" height={30} />
        <Skeleton variant="rectangular" width="100%" height={200} />
        <Skeleton variant="text" width="100%" height={30} />
        <StyledButton
          disabled
          styling={theme}
          variant="contained"
          color="primary"
        >
          Zurück
        </StyledButton>
      </div>
    );
  }

  if (error) {
    return <p>Fehler beim Laden der Daten: {error}</p>;
  }

  if (!event) {
    return <p>Event nicht gefunden oder ungültige Daten.</p>;
  }

  return (
    <div className="chapter-entry">
      <ContentContainer>
        <h3>{event.title || "Keine Überschrift verfügbar"}</h3>
      </ContentContainer>

      {hasImages && (
        <ImageContainer>
          <ImageSlider
            images={event.image_urls.map((image) => ({
              src: image.thumbnailUrl,
              height: "300px",
            }))}
            onChange={(index) => setCurrentImageIndex(index)}
          />
          {currentImage?.fullscreenUrl && (
            <FullscreenIconContainer
              onClick={() => openOverlay(currentImage.fullscreenUrl)}
            >
              <Expand size={32} />
            </FullscreenIconContainer>
          )}
        </ImageContainer>
      )}

      <HtmlRendererContainer>
        <HtmlRenderer
          texts={event.description ? [event.description] : []}
          sanitizeHtml={true}
        />
      </HtmlRendererContainer>
      <ClearFix />

      {isOverlayOpen && (
        <div className="media-overlay" onClick={closeOverlay}>
          <div
            className="media-detail-container"
            onClick={(e) => e.stopPropagation()}
          >
            {!imageLoaded && (
              <>
                <Skeleton variant="text" width="100%" height={30} />
                <Skeleton variant="rectangular" width="100%" height={400} />
              </>
            )}
            <OverlayImage
              src={selectedImageUrl}
              alt="Selected Media"
              style={{ display: imageLoaded ? "block" : "none" }}
              onLoad={() => setImageLoaded(true)}
            />
            {imageLoaded && (
              <div className="unnpcw-container details">
                {currentImage?.description && (
                  <FactText styling={theme}>
                    <StyledIconWrapper styling={styling}>
                      <Info size={18} />
                    </StyledIconWrapper>
                    <strong>Beschreibung:</strong> {currentImage.description}
                  </FactText>
                )}
              </div>
            )}
            <ButtonContainer>
              <StyledButton
                styling={theme}
                variant="contained"
                color="secondary"
                onClick={closeOverlay}
              >
                Zurück
              </StyledButton>
              <a
                href={selectedImageUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                <StyledButton
                  styling={theme}
                  variant="contained"
                  color="secondary"
                  endIcon={<Download size={18} />}
                >
                  Download
                </StyledButton>
              </a>
            </ButtonContainer>
          </div>
        </div>
      )}

      {event.category && event.category.length > 0 && (
        <Section>
          <SectionTitle styling={styling}>
            <StyledIconWrapper styling={styling}>
              <Tags size={18} />
            </StyledIconWrapper>
            Kategorien
          </SectionTitle>
          <KeywordContainer>
            {event.category.map((cat, index) => (
              <Category key={index} styling={styling}>
                {cat.name}
              </Category>
            ))}
          </KeywordContainer>
        </Section>
      )}

      {event.location?.city && (
        <Section>
          <SectionTitle styling={styling}>
            <StyledIconWrapper styling={styling}>
              <MapPin size={18} />
            </StyledIconWrapper>
            Ort
          </SectionTitle>
          <p>{event.location.city}</p>
        </Section>
      )}

      {event.startDate && event.endDate && (
        <Section>
          <SectionTitle styling={styling}>
            <StyledIconWrapper styling={styling}>
              <Calendar size={18} />
            </StyledIconWrapper>
            Zeitraum
          </SectionTitle>
          <p>
            {formatEventDate(event.startDate)} -{" "}
            {formatEventDate(event.endDate)}
          </p>
        </Section>
      )}

      {event.startTime && event.endTime && (
        <Section>
          <SectionTitle styling={styling}>
            <StyledIconWrapper styling={styling}>
              <Clock size={18} />
            </StyledIconWrapper>
            Uhrzeit
          </SectionTitle>
          <p>
            {formatTime(event.startTime)} - {formatTime(event.endTime)} Uhr
          </p>
        </Section>
      )}

      <div>
        <StyledButton
          styling={theme}
          variant="contained"
          color="primary"
          onClick={onClose}
        >
          Zurück
        </StyledButton>
      </div>
    </div>
  );
};

EventDetails.propTypes = {
  styling: PropTypes.object.isRequired,
  companyAccessname: PropTypes.string.isRequired,
  eventId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  onClose: PropTypes.func.isRequired,
};

export default EventDetails;
